import { Timestamp } from 'firebase/firestore';
import {
  getUserRole,
  getUserStatus,
  isActiveCommunityAdmin,
  isApprovedCommunityServiceAdmin,
  type AccessClaims,
  type AccessProfile,
  type CommunityServiceProviderState,
} from '../utils/accessControl';
import { UserRole, UserStatus } from '../types/firestore';

export type CommunityCategoryType =
  | 'service_forum'
  | 'ask_community'
  | 'client_voice'
  | 'gratitude'
  | 'announcements';

export type CommunityThreadKind =
  | 'discussion'
  | 'question'
  | 'client_voice'
  | 'gratitude'
  | 'announcement';

export type CommunityThreadStatus = 'open' | 'closed' | 'hidden' | 'deleted';
export type CommunityPostStatus = 'visible' | 'hidden' | 'deleted';
export type CommunityAuthorRole = 'client' | 'serviceAdmin' | 'admin';
export type CommunityReportReason =
  | 'spam'
  | 'abusive'
  | 'private_data'
  | 'off_topic'
  | 'conflict'
  | 'other';

export interface CommunityCategory {
  id: string;
  slug: string;
  title: string;
  description: string;
  type: CommunityCategoryType;
  kind: CommunityThreadKind;
  visibility: 'private' | 'protected' | 'public_preview';
  viewPolicy: 'public_preview' | 'authenticated' | 'approved_service' | 'admin';
  createPolicy: 'client' | 'approved_service' | 'admin';
  replyPolicy:
    | 'none'
    | 'approved_service'
    | 'author_and_approved_service'
    | 'service_gratitude_once';
  writerLabel: string;
  order: number;
  isActive: boolean;
}

export interface CommunityThread {
  id: string;
  categoryId: string;
  categorySlug: string;
  categoryType: CommunityCategoryType;
  kind: CommunityThreadKind;
  title: string;
  bodyExcerpt?: string;
  authorId: string;
  authorRoleSnapshot: CommunityAuthorRole;
  authorDisplayNameSnapshot: string;
  authorServiceProviderIdSnapshot?: string | null;
  authorServiceNameSnapshot?: string | null;
  authorServiceApprovedSnapshot?: boolean;
  targetServiceProviderId?: string | null;
  targetServiceNameSnapshot?: string | null;
  verifiedGratitude?: boolean;
  status: CommunityThreadStatus;
  isPinned: boolean;
  isPublicPreviewEligible: boolean;
  repliesCount: number;
  postsCount: number;
  participantsCount: number;
  reportCount: number;
  lastActivityAt?: Timestamp;
  lastPostId?: string;
  lastPostAuthorId?: string;
  lastPostAuthorDisplayNameSnapshot?: string;
  lastPostAuthorRoleSnapshot?: CommunityAuthorRole;
  createdAt?: Timestamp;
  createdBy?: string;
  updatedAt?: Timestamp;
  updatedBy?: string;
  serviceGratitudeReplyPostId?: string | null;
  serviceGratitudeRepliedAt?: Timestamp | null;
}

export interface CommunityPostQuote {
  quotedPostId: string;
  quotedAuthorId: string;
  quotedAuthorDisplayNameSnapshot: string;
  quotedExcerpt: string;
  quotedAt?: Timestamp;
  quotedUnavailable?: boolean;
}

export interface CommunityPost {
  id: string;
  threadId: string;
  categoryId: string;
  categorySlug: string;
  categoryType: CommunityCategoryType;
  body: string;
  authorId: string;
  authorRoleSnapshot: CommunityAuthorRole;
  authorDisplayNameSnapshot: string;
  authorServiceProviderIdSnapshot?: string | null;
  authorServiceNameSnapshot?: string | null;
  authorServiceApprovedSnapshot?: boolean;
  status: CommunityPostStatus;
  isOpeningPost: boolean;
  isServiceGratitudeReply?: boolean;
  quote?: CommunityPostQuote | null;
  reportCount?: number;
  createdAt?: Timestamp;
  createdBy?: string;
  updatedAt?: Timestamp;
  updatedBy?: string;
  editedAt?: Timestamp;
  editedBy?: string;
  hiddenReason?: string | null;
}

export interface CommunityReport {
  id: string;
  targetType: 'thread' | 'post';
  threadId: string;
  postId?: string | null;
  targetAuthorId?: string | null;
  reportedBy: string;
  reporterRoleSnapshot: CommunityAuthorRole;
  reason: CommunityReportReason;
  message?: string | null;
  status: 'open' | 'reviewed' | 'dismissed' | 'action_taken';
  createdAt?: Timestamp;
  reviewedAt?: Timestamp | null;
  reviewedBy?: string | null;
  resolutionNote?: string | null;
  actionTaken?: string | null;
  latestOccurrenceId?: string | null;
  latestOccurrenceVersion?: number | null;
}

export interface CommunityReportOccurrence {
  id: string;
  reportId: string;
  occurrenceVersion: number;
  targetType: 'thread' | 'post';
  threadId: string;
  postId?: string | null;
  reportedBy: string;
  createdAt?: Timestamp;
  statusAtCreation: 'open';
  isReReport: boolean;
  hasLegacyPredecessor: boolean;
  resolutionStatus: 'reviewed' | 'dismissed' | 'action_taken' | null;
  resolvedAt?: Timestamp | null;
  resolvedBy?: string | null;
}

export const COMMUNITY_CATEGORIES: CommunityCategory[] = [
  {
    id: 'forum-serwisantow',
    slug: 'forum-serwisantow',
    title: 'Forum serwisantów',
    description:
      'Branżowe rozmowy zatwierdzonych serwisów: praktyka, części, diagnoza i dobre standardy.',
    type: 'service_forum',
    kind: 'discussion',
    visibility: 'private',
    viewPolicy: 'approved_service',
    createPolicy: 'approved_service',
    replyPolicy: 'approved_service',
    writerLabel: 'Piszą zatwierdzeni serwisanci',
    order: 10,
    isActive: true,
  },
  {
    id: 'zapytaj-spolecznosc',
    slug: 'zapytaj-spolecznosc',
    title: 'Zapytaj społeczność',
    description:
      'Ogólne pytania klientów do społeczności. To nie zastępuje pilnej obsługi konkretnego zgłoszenia.',
    type: 'ask_community',
    kind: 'question',
    visibility: 'protected',
    viewPolicy: 'authenticated',
    createPolicy: 'client',
    replyPolicy: 'author_and_approved_service',
    writerLabel: 'Klient pyta, serwis odpowiada',
    order: 20,
    isActive: true,
  },
  {
    id: 'glos-klienta',
    slug: 'glos-klienta',
    title: 'Głos klienta',
    description:
      'Spokojny sygnał dla społeczności: problem, obserwacja albo potrzeba. Bez publicznego piętnowania.',
    type: 'client_voice',
    kind: 'client_voice',
    visibility: 'protected',
    viewPolicy: 'authenticated',
    createPolicy: 'client',
    replyPolicy: 'author_and_approved_service',
    writerLabel: 'Klient opisuje, serwis odpowiada rzeczowo',
    order: 30,
    isActive: true,
  },
  {
    id: 'podziekowania',
    slug: 'podziekowania',
    title: 'Podziękowania',
    description: 'Miejsce na krótkie, ogólne podziękowanie. To nie jest system ocen ani ranking.',
    type: 'gratitude',
    kind: 'gratitude',
    visibility: 'public_preview',
    viewPolicy: 'public_preview',
    createPolicy: 'client',
    replyPolicy: 'service_gratitude_once',
    writerLabel: 'Klient dziękuje, wskazany serwis może odpowiedzieć raz',
    order: 40,
    isActive: true,
  },
  {
    id: 'ogloszenia',
    slug: 'ogloszenia',
    title: 'Ogłoszenia',
    description: 'Ważne informacje administracyjne dla społeczności Ratuj Sprzęt.',
    type: 'announcements',
    kind: 'announcement',
    visibility: 'public_preview',
    viewPolicy: 'public_preview',
    createPolicy: 'admin',
    replyPolicy: 'none',
    writerLabel: 'Publikuje administracja',
    order: 50,
    isActive: true,
  },
];

export const COMMUNITY_CATEGORY_BY_SLUG = new Map(
  COMMUNITY_CATEGORIES.map((category) => [category.slug, category])
);

export const canEnterProtectedCommunity = (
  userProfile?: AccessProfile | null,
  claims?: AccessClaims | null
): boolean => {
  if (getUserStatus(userProfile, claims) !== UserStatus.ACTIVE) return false;
  const role = getUserRole(userProfile, claims);
  return role === UserRole.CLIENT || role === UserRole.SERVICE_ADMIN || role === UserRole.ADMIN;
};

const hasExactActiveCommunityProfile = (userProfile?: AccessProfile | null): boolean =>
  userProfile?.status === UserStatus.ACTIVE;

const isExactActiveCommunityAdmin = (
  userProfile?: AccessProfile | null,
  claims?: AccessClaims | null
): boolean => hasExactActiveCommunityProfile(userProfile) && claims?.isAdmin === true;

const isExactApprovedCommunityServiceAdmin = (
  userProfile?: AccessProfile | null,
  claims?: AccessClaims | null,
  provider?: CommunityServiceProviderState | null
): boolean =>
  hasExactActiveCommunityProfile(userProfile) &&
  userProfile?.role === UserRole.SERVICE_ADMIN &&
  (claims?.isServiceAdmin === true || claims?.isServiceProvider === true) &&
  provider?.verificationStatus === 'approved' &&
  provider.isVerified === true &&
  provider.isTestData !== true;

export const canViewCommunityCategory = (
  category: CommunityCategory,
  userProfile?: AccessProfile | null,
  claims?: AccessClaims | null,
  provider?: CommunityServiceProviderState | null
): boolean => {
  if (category.viewPolicy === 'public_preview') return true;
  if (!userProfile) return false;
  if (isExactActiveCommunityAdmin(userProfile, claims)) return true;
  if (!hasExactActiveCommunityProfile(userProfile)) return false;
  if (category.viewPolicy === 'authenticated') return true;
  if (category.viewPolicy === 'approved_service') {
    return isExactApprovedCommunityServiceAdmin(userProfile, claims, provider);
  }
  return false;
};

export const getAccessibleCommunityCategories = (
  categories: CommunityCategory[],
  userProfile?: AccessProfile | null,
  claims?: AccessClaims | null,
  provider?: CommunityServiceProviderState | null
): CommunityCategory[] =>
  categories.filter((category) =>
    canViewCommunityCategory(category, userProfile, claims, provider)
  );

export const canCreateCommunityThreadInCategory = (
  category: CommunityCategory,
  userProfile?: AccessProfile | null,
  claims?: AccessClaims | null,
  provider?: CommunityServiceProviderState | null
): boolean => {
  if (getUserStatus(userProfile, claims) !== UserStatus.ACTIVE) return false;
  if (isActiveCommunityAdmin(userProfile, claims)) return true;
  const role = getUserRole(userProfile, claims);
  if (category.createPolicy === 'client') return role === UserRole.CLIENT;
  if (category.createPolicy === 'approved_service') {
    return isApprovedCommunityServiceAdmin(userProfile, claims, provider);
  }
  return false;
};

export const canViewCommunityThread = (
  userProfile: AccessProfile | null | undefined,
  claims: AccessClaims | null | undefined,
  thread: CommunityThread,
  provider?: CommunityServiceProviderState | null
): boolean => {
  const isVisibleStatus = thread.status === 'open' || thread.status === 'closed';
  if (
    isVisibleStatus &&
    thread.isPublicPreviewEligible === true &&
    (thread.categoryType === 'gratitude' || thread.categoryType === 'announcements')
  ) {
    return true;
  }
  if (!userProfile || !hasExactActiveCommunityProfile(userProfile)) return false;
  if (isExactActiveCommunityAdmin(userProfile, claims)) return true;
  if (!isVisibleStatus) return false;
  if (thread.categoryType !== 'service_forum') return true;
  return isExactApprovedCommunityServiceAdmin(userProfile, claims, provider);
};

export const canReplyToCommunityThread = (
  thread: CommunityThread,
  currentUid: string | undefined | null,
  userProfile?: AccessProfile | null,
  claims?: AccessClaims | null,
  provider?: CommunityServiceProviderState | null
): boolean => {
  const category = COMMUNITY_CATEGORY_BY_SLUG.get(thread.categorySlug);
  if (!category || thread.status !== 'open') return false;
  if (getUserStatus(userProfile, claims) !== UserStatus.ACTIVE) return false;
  if (isActiveCommunityAdmin(userProfile, claims) && category.replyPolicy !== 'none') return true;
  if (category.replyPolicy === 'approved_service') {
    return isApprovedCommunityServiceAdmin(userProfile, claims, provider);
  }
  if (category.replyPolicy === 'author_and_approved_service') {
    return (
      isApprovedCommunityServiceAdmin(userProfile, claims, provider) ||
      Boolean(currentUid && thread.authorId && currentUid === thread.authorId)
    );
  }
  if (category.replyPolicy === 'service_gratitude_once') {
    return (
      isApprovedCommunityServiceAdmin(userProfile, claims, provider) &&
      thread.targetServiceProviderId === currentUid &&
      !thread.serviceGratitudeReplyPostId
    );
  }
  return false;
};

export const COMMUNITY_REPORT_REASONS: Array<{ value: CommunityReportReason; label: string }> = [
  { value: 'spam', label: 'Spam' },
  { value: 'abusive', label: 'Obraźliwe' },
  { value: 'private_data', label: 'Dane prywatne' },
  { value: 'off_topic', label: 'Poza tematem' },
  { value: 'conflict', label: 'Konflikt / reklamacja' },
  { value: 'other', label: 'Inne' },
];

export const formatCommunityDate = (value?: Timestamp | null): string => {
  const date = value?.toDate?.();
  if (!date) return 'brak daty';
  return date.toLocaleDateString('pl-PL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export const getCommunityRoleLabel = (role?: CommunityAuthorRole): string => {
  if (role === 'admin') return 'Admin';
  if (role === 'serviceAdmin') return 'Serwis';
  return 'Klient';
};

const normalizeQuoteText = (value: unknown): string =>
  typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : '';

const truncateQuoteText = (value: string, maxLength: number): string => {
  if (value.length <= maxLength) return value;
  if (maxLength <= 3) return value.slice(0, maxLength);
  return `${value.slice(0, maxLength - 3)}...`;
};

export const normalizeCommunityPostQuote = (value: unknown): CommunityPostQuote | null => {
  if (!value || typeof value !== 'object') return null;
  const quote = value as Record<string, unknown>;
  const quotedPostId = normalizeQuoteText(quote.quotedPostId || quote.postId || quote.id);
  const quotedAuthorId = normalizeQuoteText(quote.quotedAuthorId || quote.authorId);
  const quotedAuthorDisplayNameSnapshot = normalizeQuoteText(
    quote.quotedAuthorDisplayNameSnapshot || quote.authorDisplayNameSnapshot || quote.authorName
  );
  const quotedExcerpt = truncateQuoteText(
    normalizeQuoteText(quote.quotedExcerpt || quote.body || quote.excerpt),
    400
  );

  if (!quotedPostId || !quotedAuthorDisplayNameSnapshot || !quotedExcerpt) {
    return {
      quotedPostId,
      quotedAuthorId,
      quotedAuthorDisplayNameSnapshot: quotedAuthorDisplayNameSnapshot || 'Cytowany wpis',
      quotedExcerpt: 'Cytowany wpis jest niedostępny.',
      quotedAt: quote.quotedAt as Timestamp | undefined,
      quotedUnavailable: true,
    };
  }

  return {
    quotedPostId,
    quotedAuthorId,
    quotedAuthorDisplayNameSnapshot,
    quotedExcerpt,
    quotedAt: quote.quotedAt as Timestamp | undefined,
  };
};
